import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Loader2,
  RotateCcw,
  Search as SearchIcon,
  StickyNote,
  Trash2,
  FileText,
  History,
  Sparkles,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, StatusPill, fmtDate, fmtDateTime } from "./_shared";
import Avatar from "@/components/Avatar";
import GrantSubscriptionDialog from "./users/GrantSubscriptionDialog";

export default function AdminUserDetail() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);
  const [rerunningAI, setRerunningAI] = useState(false);
  const [rerunningJobs, setRerunningJobs] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  const openAsClient = async () => {
    const targetEmail = detail?.user?.email || userId;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Open ${targetEmail} as a client?\n\n` +
          "A new tab will open signed in as this client. Use the amber " +
          "banner's 'Exit' button (or the navbar pill if hidden) to come " +
          "back to your admin session.\n\n" +
          "Tip: open in a separate Incognito window for true side-by-side.",
      )
    ) {
      return;
    }
    setImpersonating(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/impersonate`);
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't create impersonation link.",
      );
    } finally {
      setImpersonating(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [d, n, h] = await Promise.all([
        api.get(`/admin/users/${userId}`),
        api.get(`/admin/users/${userId}/notes`),
        api.get(`/admin/users/${userId}/profile-history`),
      ]);
      setDetail(d.data);
      setNotes(n.data);
      setHistory(h.data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load user.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/notes`, {
        body: noteText.trim(),
      });
      setNotes((arr) => [data, ...arr]);
      setNoteText("");
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to add note.");
    } finally {
      setPosting(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/admin/notes/${noteId}`);
      setNotes((arr) => arr.filter((n) => n.note_id !== noteId));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to delete note.");
    }
  };

  const rerunAI = async () => {
    setRerunningAI(true);
    try {
      await api.post(`/admin/assessments/${userId}/rerun-ai`);
      toast.success("AI re-analysis complete");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to rerun AI.");
    } finally {
      setRerunningAI(false);
    }
  };

  const rerunJobs = async () => {
    setRerunningJobs(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/rerun-jobs`);
      toast.success(`Job search complete (${data.match?.jobs?.length || 0} roles)`);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to rerun jobs.");
    } finally {
      setRerunningJobs(false);
    }
  };

  if (loading || !detail) {
    return (
      <AdminLayout title="User" subtitle={userId}>
        <div
          className="flex items-center gap-2 text-slate-500 text-sm"
          data-testid="admin-user-detail-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading user...
        </div>
      </AdminLayout>
    );
  }

  const u = detail.user;
  const s = detail.assessment_summary || {};
  const sub = detail.subscription;

  return (
    <AdminLayout
      title={u.name || u.email}
      subtitle={u.email}
      actions={
        <div className="flex items-center gap-2">
          {u.role !== "admin" && (
            <button
              type="button"
              onClick={openAsClient}
              disabled={impersonating}
              title="Open this user's view in a new tab so you can see and interact with their profile as if you were them"
              className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              data-testid="admin-user-detail-impersonate-button"
            >
              {impersonating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserCheck className="h-4 w-4" />
              )}
              Open as client
            </button>
          )}
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="admin-user-back-link"
          >
            <ChevronLeft className="h-4 w-4" /> All users
          </Link>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-user-detail">
        <Card className="p-5 lg:col-span-1" testid="admin-user-profile-card">
          <div className="flex items-center gap-3 mb-4">
            <Avatar
              user={u}
              size="lg"
              testid={`admin-user-detail-avatar-${u.user_id}`}
            />
            <div className="min-w-0">
              <h2 className="text-sm font-medium text-slate-900 truncate">
                {u.name || u.email.split("@")[0]}
              </h2>
              <p className="text-xs text-slate-500 truncate">{u.email}</p>
            </div>
          </div>
          <dl className="text-sm space-y-2">
            <Row label="User ID">
              <span className="font-mono text-xs">{u.user_id}</span>
            </Row>
            <Row label="Current role">
              {u.current_role_title ? (
                <div className="min-w-0">
                  <div className="text-slate-900 truncate">{u.current_role_title}</div>
                  {u.current_industry && (
                    <div className="text-xs text-slate-500 truncate">{u.current_industry}</div>
                  )}
                </div>
              ) : (
                <span className="text-xs text-slate-400 italic">Not yet provided</span>
              )}
            </Row>
            <Row label="Role">
              <StatusPill value={u.role || "user"} tone={u.role === "admin" ? "amber" : "slate"} />
            </Row>
            <Row label="Sign-in">{u.auth_provider || "password"}</Row>
            <Row label="Created">{fmtDate(u.created_at)}</Row>
            {u.has_resume_pdf && (
              <Row label="Résumé">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/archive/resume/${u.user_id}.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
                  data-testid={`admin-user-detail-resume-pdf-${u.user_id}`}
                >
                  <FileText className="h-3 w-3" /> Open PDF
                </a>
              </Row>
            )}
          </dl>

          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={rerunAI}
              disabled={rerunningAI || !s.assessment_id}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              data-testid="admin-user-rerun-ai-button"
            >
              <RotateCcw className={`h-4 w-4 ${rerunningAI ? "animate-spin" : ""}`} />
              {rerunningAI ? "Rerunning AI..." : "Rerun AI analysis"}
            </button>
            <button
              type="button"
              onClick={rerunJobs}
              disabled={rerunningJobs || !s.assessment_id}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              data-testid="admin-user-rerun-jobs-button"
            >
              <SearchIcon className={`h-4 w-4 ${rerunningJobs ? "animate-spin" : ""}`} />
              {rerunningJobs ? "Searching..." : "Rerun job search"}
            </button>
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2" testid="admin-user-assessment-card">
          <header className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-900">Assessment</h2>
            {s.assessment_id && (
              <Link
                to={`/admin/assessments/${userId}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
                data-testid="admin-user-view-assessment-link"
              >
                <FileText className="h-3.5 w-3.5" /> Open full assessment
              </Link>
            )}
          </header>
          {!s.assessment_id ? (
            <p className="text-sm text-slate-500" data-testid="admin-user-no-assessment">
              This user has not started an assessment.
            </p>
          ) : (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Row label="Status">
                <StatusPill
                  value={s.status}
                  tone={s.status === "processed" ? "green" : s.status === "submitted" ? "sky" : "slate"}
                />
              </Row>
              <Row label="Updated">{fmtDateTime(s.updated_at)}</Row>
              <Row label="Current role">{s.current_role || "—"}</Row>
              <Row label="Industry">{s.industry || "—"}</Row>
              <Row label="City">{s.city || "—"}</Row>
              <Row label="AI report">{s.has_ai_report ? "Yes" : "No"}</Row>
              <Row label="Pathway · now">{s.pathway_immediate || "—"}</Row>
              <Row label="Pathway · transitional">{s.pathway_transitional || "—"}</Row>
              <Row label="Pathway · long-term">{s.pathway_long_term || "—"}</Row>
              <Row label="Latest job search">
                {detail.latest_job_match_at ? fmtDateTime(detail.latest_job_match_at) : "—"}
              </Row>
            </dl>
          )}
        </Card>

        <Card className="p-5 lg:col-span-1" testid="admin-user-subscription-card">
          <header className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-900">Subscription</h2>
            <button
              type="button"
              onClick={() => setGrantOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100"
              data-testid="admin-user-grant-sub-button"
              title="Activate a membership or archive plan bypassing Stripe"
            >
              <Sparkles className="h-3 w-3" /> Grant
            </button>
          </header>
          {!sub ? (
            <p className="text-sm text-slate-500">No subscription record.</p>
          ) : (
            <dl className="text-sm space-y-2">
              <Row label="Status">
                <StatusPill
                  value={sub.status}
                  tone={
                    sub.status === "active"
                      ? "green"
                      : sub.status === "cancelling"
                        ? "amber"
                        : "slate"
                  }
                />
              </Row>
              <Row label="Months paid">{sub.months_paid ?? 0}</Row>
              <Row label="Started">{sub.started_at ? fmtDate(sub.started_at) : "—"}</Row>
              <Row label="Period end">
                {sub.current_period_end ? fmtDate(sub.current_period_end) : "—"}
              </Row>
              <Row label="Cancel at end">{sub.cancel_at_period_end ? "Yes" : "No"}</Row>
            </dl>
          )}
        </Card>

        {/* Profile change history — append-only audit trail of email / phone / name edits. */}
        <Card
          className="p-5 lg:col-span-3"
          testid="admin-user-profile-history-card"
        >
          <header className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-900 inline-flex items-center gap-2">
              <History className="h-4 w-4" /> Profile changes
            </h2>
            <span className="text-xs text-slate-500" data-testid="admin-user-profile-history-count">
              {history.length}
            </span>
          </header>
          {history.length === 0 ? (
            <p
              className="text-sm text-slate-500"
              data-testid="admin-user-profile-history-empty"
            >
              No edits recorded yet.
            </p>
          ) : (
            <ul
              className="divide-y divide-slate-100"
              data-testid="admin-user-profile-history-list"
            >
              {history.map((h) => (
                <li
                  key={h.history_id}
                  className="py-2.5 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm"
                  data-testid={`admin-user-profile-history-${h.history_id}`}
                >
                  <span className="text-xs text-slate-500">
                    {fmtDateTime(h.changed_at)}
                  </span>
                  <span className="text-slate-900 font-medium capitalize">
                    {h.field}
                  </span>
                  <span className="text-slate-600 truncate" title={h.previous_value || "—"}>
                    <span className="text-slate-400">from</span>{" "}
                    <span className="line-through">{h.previous_value || "—"}</span>
                  </span>
                  <span className="text-slate-800 truncate" title={h.new_value || "—"}>
                    <span className="text-slate-400">to</span>{" "}
                    <span className="font-medium">{h.new_value || "—"}</span>
                    <span className="ml-2 text-[11px] text-slate-400">
                      by {h.changed_by_email}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5 lg:col-span-2" testid="admin-user-notes-card">
          <header className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-900 inline-flex items-center gap-2">
              <StickyNote className="h-4 w-4" /> Candidate notes
            </h2>
            <span className="text-xs text-slate-500">{notes.length}</span>
          </header>
          <form onSubmit={addNote} className="mb-4" data-testid="admin-user-add-note-form">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
              placeholder="Add a private note about this candidate..."
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              data-testid="admin-user-note-input"
            />
            <div className="flex items-center justify-end mt-2">
              <button
                type="submit"
                disabled={posting || !noteText.trim()}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                data-testid="admin-user-note-submit-button"
              >
                {posting ? "Saving..." : "Add note"}
              </button>
            </div>
          </form>
          {notes.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="admin-user-notes-empty">
              No notes yet.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="admin-user-notes-list">
              {notes.map((n) => (
                <li
                  key={n.note_id}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
                  data-testid={`admin-user-note-${n.note_id}`}
                >
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>
                      {n.author_email} · {fmtDateTime(n.created_at)}
                    </span>
                    <button
                      type="button"
                      onClick={() => deleteNote(n.note_id)}
                      className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1"
                      data-testid={`admin-user-note-delete-${n.note_id}`}
                    >
                      <Trash2 className="h-3 w-3" /> delete
                    </button>
                  </div>
                  <p className="text-slate-800 whitespace-pre-wrap">{n.body}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {grantOpen && (
        <GrantSubscriptionDialog
          userId={userId}
          userEmail={detail?.user?.email || userId}
          onClose={() => setGrantOpen(false)}
          onGranted={refresh}
        />
      )}
    </AdminLayout>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800 text-right max-w-[60%] truncate">{children}</dd>
    </div>
  );
}
